/**
 * Global state management for shortlist view UI using signals
 *
 * This store centralizes all shortlist view UI state that was previously managed locally in ShortlistView.
 * It provides a single source of truth for filters, dialogs, form inputs, and drag-and-drop state.
 *
 * Key features:
 * 1. Filter type management
 * 2. Dialog visibility states
 * 3. Form input state (new group, edit group)
 * 4. Drag and drop state (used with dnd-kit for visual feedback)
 */

import type { ShortlistItemType } from '@elara/domain/models/schemas/shortlist-schemas';

import { createState } from '../lib/signals-utils.js';

export type FilterType = 'all' | ShortlistItemType;

/**
 * The main reactive store for shortlist view UI state
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export function createShortlistUIState() {
  const state = createState({
    /** Current filter type */
    filterType: 'all' as FilterType,

    /** Whether the create group dialog is open */
    createGroupOpen: false,

    /** New group name input value */
    newGroupName: '',

    /** ID of the group currently being edited, or null if none */
    editingGroupId: null as string | null,

    /** Edit group name input value */
    editGroupName: '',

    /** ID of the item currently being dragged, or null if none */
    draggedItemId: null as string | null,

    /** ID of the group currently being dragged, or null if none */
    draggedGroupId: null as string | null,

    /** ID of the group currently being dragged over, or null if none */
    dragOverGroupId: null as string | null,

    /** ID of the group that is an invalid drop target, or null if none */
    invalidDropGroupId: null as string | null,

    /** ID of the currently active dragged item or group (for DndContext) */
    activeId: null as string | null,

    /** Drop preview index showing where item will be dropped { groupId, index } */
    dropPreviewIndex: null as { groupId: string; index: number } | null,

    /** Last over ID for collision detection fallback */
    lastOverId: null as string | null,

    /** Current view mode - board (kanban) or map */
    viewMode: 'board' as 'board' | 'map',

    /** Selected group ID for map view filtering (null = show all groups) */
    mapGroupFilter: null as string | null,

    /** Sets the filter type */
    setFilterType(filterType: FilterType) {
      this.filterType = filterType;
    },

    /** Opens the create group dialog */
    openCreateGroupDialog() {
      this.createGroupOpen = true;
    },

    /** Closes the create group dialog */
    closeCreateGroupDialog() {
      this.createGroupOpen = false;
      this.newGroupName = '';
    },

    /** Sets the new group name */
    setNewGroupName(name: string) {
      this.newGroupName = name;
    },

    /** Starts editing a group */
    startEditingGroup(groupId: string, currentName: string) {
      this.editingGroupId = groupId;
      this.editGroupName = currentName;
    },

    /** Stops editing the current group */
    stopEditingGroup() {
      this.editingGroupId = null;
      this.editGroupName = '';
    },

    /** Sets the edit group name */
    setEditGroupName(name: string) {
      this.editGroupName = name;
    },

    /** Starts dragging an item */
    startDrag(itemId: string) {
      this.draggedItemId = itemId;
    },

    /** Starts dragging a group */
    startDragGroup(groupId: string) {
      this.draggedGroupId = groupId;
    },

    /** Ends dragging */
    endDrag() {
      this.draggedItemId = null;
      this.dragOverGroupId = null;
      this.invalidDropGroupId = null;
      this.clearDragState();
    },

    /** Ends dragging a group */
    endDragGroup() {
      this.draggedGroupId = null;
      this.clearDragState();
    },

    /** Sets the group being dragged over */
    setDragOverGroup(groupId: string | null) {
      this.dragOverGroupId = groupId;
      this.invalidDropGroupId = null;
    },

    /** Sets the invalid drop group */
    setInvalidDropGroup(groupId: string | null) {
      this.invalidDropGroupId = groupId;
      this.dragOverGroupId = null;
    },

    /** Clears drag over state */
    clearDragOver() {
      this.dragOverGroupId = null;
      this.invalidDropGroupId = null;
    },

    /** Sets the active dragged item/group ID */
    setActiveId(id: string | null) {
      this.activeId = id;
    },

    /** Sets the drop preview index */
    setDropPreviewIndex(index: { groupId: string; index: number } | null) {
      this.dropPreviewIndex = index;
    },

    /** Sets the last over ID for collision detection */
    setLastOverId(id: string | null) {
      this.lastOverId = id;
    },

    /** Clears all drag-related state (activeId, preview, etc.) */
    clearDragState() {
      this.activeId = null;
      this.dropPreviewIndex = null;
      this.lastOverId = null;
    },

    /** Sets the view mode */
    setViewMode(viewMode: 'board' | 'map') {
      this.viewMode = viewMode;
    },

    /** Sets the map group filter (null = show all groups) */
    setMapGroupFilter(groupId: string | null) {
      this.mapGroupFilter = groupId;
    },

    /** Resets all UI state */
    reset() {
      this.filterType = 'all';
      this.createGroupOpen = false;
      this.newGroupName = '';
      this.editingGroupId = null;
      this.editGroupName = '';
      this.draggedItemId = null;
      this.draggedGroupId = null;
      this.dragOverGroupId = null;
      this.invalidDropGroupId = null;
      this.clearDragState();
      this.viewMode = 'board';
      this.mapGroupFilter = null;
    },
  });

  return state;
}

export type ShortlistUIState = ReturnType<typeof createShortlistUIState>;

